import { ChangeEvent, useState } from 'react';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input/mobile';
import { ErrorType, recordError } from '../../../../models/apiWrappers/api/exceptions';
import { useMst } from '../../../../models/rootModel';
import { SignUpData } from '../../../../models/types/UserDiscoveryResponse.types';
import { useAnalytics } from '../../../../utils/analytics';
import { generateRandomNumber } from '../../../../utils/dataGenerators/generateRandomNumber';
import { generateRandomString } from '../../../../utils/dataGenerators/generateRandomString';
import { sendGTMEvent } from '../../../../utils/googleTagManager';
import { StepPropsV2 } from '../../../common/v2/BaseStepV2.types';
import { ErrorCode, ServerError, SignUpFields, SignUpValues } from '../../SignUp/SignUp.types';
import { emailValidator } from '../../../../utils/validators/emailValidator';
import { generateRandomEmail } from '../../../../utils/dataGenerators/generateRandomEmail';
import { PHONE_INPUT_MAX_LENGTH, TEXT_INPUT_MAX_LENGTH } from '../../../common/InputField/InputField.const';

interface UseSignupParams {
  useMockedSignupData: boolean;
  stepProps: StepPropsV2 & { title?: string };
}

export const useSignup = ({ useMockedSignupData, stepProps }: UseSignupParams) => {
  const analytics = useAnalytics();
  const { UserDiscoveryResponse, UserPayment, Step, PlanLabels } = useMst();

  const [signUpValues, setSignUpValues] = useState<SignUpValues>({
    [SignUpFields.Name]:
      UserDiscoveryResponse.userName.length > 0
        ? UserDiscoveryResponse.userName
        : useMockedSignupData
        ? generateRandomString(6)
        : '',
    [SignUpFields.Phone]:
      UserDiscoveryResponse.userPhoneNumber.length > 0
        ? UserDiscoveryResponse.userPhoneNumber
        : useMockedSignupData
        ? `+1202${generateRandomNumber(100, 999)}${generateRandomNumber(1000, 9999)}`
        : '',
    [SignUpFields.Email]:
      UserDiscoveryResponse.userEmail.length > 0
        ? UserDiscoveryResponse.userEmail
        : useMockedSignupData
        ? generateRandomEmail()
        : '',
  });
  const [signUpInputError, setSignUpInputError] = useState<SignUpValues>({
    [SignUpFields.Name]: '',
    [SignUpFields.Phone]: '',
    [SignUpFields.Email]: '',
  });
  const [isSignupErrorPopUpOpen, setIsSignupErrorPopUpOpen] = useState(false);
  const [isSignupBeingProcessed, setisSignupBeingProcessed] = useState(false);
  const [signupServerErrorMessage, setSignupServerErrorMessage] = useState<ServerError>();

  const isUserSignedUp = Boolean(UserDiscoveryResponse.accountId);

  const handleSignupInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (value.length > TEXT_INPUT_MAX_LENGTH) {
      return;
    }

    // Doesn't allow to enter number or other illegal chars, allows space, letters and ' -
    if (name === 'name' && /([\d!@#$%^&*()_+={}"|\\/[\]`,.?])+/.test(value)) {
      return;
    }
    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [name]: '',
    }));

    setSignUpValues((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const handleSignupPhoneChange = (value: string | undefined) => {
    if ((value?.length ?? 0) > PHONE_INPUT_MAX_LENGTH) {
      return;
    }

    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [SignUpFields.Phone]: '',
    }));
    setSignUpValues((prevState) => {
      return { ...prevState, [SignUpFields.Phone]: value ?? '' };
    });
  };

  const onSignupErrorClosePopup = () => {
    setIsSignupErrorPopUpOpen(false);
    if (signupServerErrorMessage?.errorCode === ErrorCode.Exists) {
      window.open(
        `${process.env.REACT_APP_API_LOGIN}login/?loginRedirectUrl=${encodeURIComponent(window.location.href)}`,
        '_self'
      );
    }
  };

  const validateSignupForm = () => {
    let isThereError = false;

    const name = signUpValues[SignUpFields.Name].trim();
    if (name.length === 0) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        name: 'Please enter your name',
      }));
    }

    if (name.length > TEXT_INPUT_MAX_LENGTH) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        name: `Name is too long (${TEXT_INPUT_MAX_LENGTH} is max)`,
      }));
    }

    if (!isValidPhoneNumber(signUpValues[SignUpFields.Phone])) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        phone: 'Please enter your correct phone number',
      }));
    }

    const emailValidationResult = emailValidator(signUpValues[SignUpFields.Email]);

    if (emailValidationResult.error) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: emailValidationResult.errorMessage,
      }));
    }

    return !isThereError;
  };

  const checkIfCanSignup = async () => {
    try {
      const parsedPhone = parsePhoneNumber(signUpValues[SignUpFields.Phone]);

      if (!parsedPhone) {
        return false;
      }

      const params = {
        phone: parsedPhone.nationalNumber,
        country_code: `+${parsedPhone.countryCallingCode}`,
        email: signUpValues[SignUpFields.Email],
      };

      const urlParams = new URLSearchParams(params);
      const resCanUserSignUp = await UserDiscoveryResponse.canUserSignUp(urlParams.toString());
      if (resCanUserSignUp.kind !== 'ok') {
        throw new Error(resCanUserSignUp.data.error || ErrorCode.InternalServerError);
      }

      return true;
    } catch (error) {
      if (error instanceof Error) {
        const message =
          error.message === ErrorCode.Exists
            ? 'Welcome back! We found an existing account with your contact details. Please click on the button below to log back in.'
            : 'Sorry, something went wrong...';
        setSignupServerErrorMessage({ message, errorCode: ErrorCode.Exists });
        setIsSignupErrorPopUpOpen(true);
        setisSignupBeingProcessed(false);
      }
      return false;
    }
  };

  const signUp = async (): Promise<{ result: false; errorMessage?: string } | { result: true; accountId: string }> => {
    if (isUserSignedUp) {
      return { result: true, accountId: UserDiscoveryResponse.accountId! };
    }

    try {
      setisSignupBeingProcessed(true);
      const parsedPhone = parsePhoneNumber(signUpValues[SignUpFields.Phone]);

      if (!parsedPhone) {
        return { result: false };
      }

      const canSignup = await checkIfCanSignup();

      if (!canSignup) {
        return { result: false };
      }

      UserDiscoveryResponse.setUserNameAndEmail({
        email: signUpValues[SignUpFields.Email].trim(),
        name: signUpValues[SignUpFields.Name].trim(),
      });
      UserDiscoveryResponse.setUserPhoneNumber(signUpValues[SignUpFields.Phone].trim());

      const dataToSend: SignUpData = UserDiscoveryResponse.signUpDateToSend;

      // CRCL-5661: we either get plan labels ids from web-quiz flow or from voices-v4 flow
      dataToSend.discovery.planLabelsIds = dataToSend.discovery?.planLabelsIds?.length
        ? dataToSend.discovery?.planLabelsIds
        : PlanLabels.selectedPlanLabelsIds;

      console.log({
        planLabelsIds: dataToSend.discovery.planLabelsIds,
        selectedPlanLabelsIds: PlanLabels.selectedPlanLabelsIds,
      });

      if (UserPayment.appliedCouponCode) {
        dataToSend.couponCode = UserPayment.appliedCouponCode;
      }

      const resSignUpUser = await UserDiscoveryResponse.signUpUser({ ...dataToSend, origin: Step.origin ?? '' });

      const { kind, data } = resSignUpUser;

      if (kind !== 'ok') {
        const errorMessage = data?._uiNotice ? data._uiNotice.text[0] : 'Unexpected error while registering user!';
        return {
          result: false,
          errorMessage,
        };
      }

      const { accountId } = data;

      sendGTMEvent({
        event: 'signup_completed',
        user_id: accountId,
        phone: dataToSend.phone,
        full_name: dataToSend.name,
        email: dataToSend.email,
      });

      analytics.track(`click signUp complete ${stepProps.type}`, {
        stepId: stepProps.id,
        stepType: stepProps.type,
        stepIndex: stepProps.stepIndex,
        stepTitle: stepProps.title ?? '',
        ...UserDiscoveryResponse.utms,
        origin: Step.origin,
        userID: accountId,
      });

      return { result: true, accountId };
    } catch (error) {
      recordError(ErrorType.HANDLED, error);
      if (error instanceof Error) {
        const message = 'Sorry, something went wrong...';
        setSignupServerErrorMessage({ message, errorCode: ErrorCode.Exists });
        setIsSignupErrorPopUpOpen(true);
      }
      return { result: false };
    } finally {
      setisSignupBeingProcessed(false);
    }
  };

  return {
    signUpValues,
    signUpInputError,
    handleSignupInputChange,
    handleSignupPhoneChange,
    validateSignupForm,
    signUp,
    isSignupErrorPopUpOpen,
    onSignupErrorClosePopup,
    setIsSignupErrorPopUpOpen,
    signupServerErrorMessage,
    isSignupBeingProcessed,
    checkIfCanSignup,
    isUserSignedUp,
  };
};
